import React, { useEffect, useCallback } from 'react';
import {
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  Tooltip,
} from '@mui/material';
import { withTransaction } from '@elastic/apm-rum-react';
import { useFormik } from 'formik';
import { object, number, ref } from 'yup';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import useOrganization from '../../hooks/UseOrganization';

const LABEL_STYLES = {
  '.MuiInputLabel-root': {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    width: '100%',
    justifyContent: 'space-between',
  },
  '.MuiInputLabel-root > span': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: 'calc(100% - 40px)',
  },
};

const PasswordSetting = () => {
  const orgName = useSelector((state) => state.common.orgName);
  const theme = useTheme();
  const { updatePassword, getPassword } = useOrganization();

  const formikForm = useFormik({
    initialValues: {
      expPassword: 15,
      minLength: 8,
      splChar: 0,
      upperChar: 0,
      lowerChar: 0,
      maxLength: 12,
    },
    validationSchema: object().shape({
      expPassword: number()
        .max(366, 'Value must be less than or equal to 366')
        .integer('Value must be an integer')
        .min(7, 'Value must be greater than or equal to 7')
        .required('Required'),
      minLength: number()
        .max(15, 'Value must be less than or equal to 15')
        .integer('Value must be an integer')
        .min(5, 'Value must be greater than or equal to 5')
        .required('Required'),
      splChar: number()
        .max(5, 'Value must be less than or equal to 5')
        .integer('Value must be an integer')
        .min(0, 'Value must be greater than or equal to 0')
        .required('Required'),
      upperChar: number()
        .max(5, 'Value must be less than or equal to 5')
        .integer('Value must be an integer')
        .min(0, 'Value must be greater than or equal to 0')
        .required('Required'),
      lowerChar: number()
        .max(5, 'Value must be less than or equal to 5')
        .integer('Value must be an integer')
        .min(0, 'Value must be greater than or equal to 0')
        .required('Required'),
      maxLength: number()
        .max(64, 'Value must be less than or equal to 64')
        .integer('Value must be an integer')
        .min(5, 'Value must be greater than or equal to 5')
        .moreThan(
          ref('minLength'),
          'Max length must be greater than Min length'
        )
        .required('Required'),
    }),
    onSubmit: (values) => {
      const payload = {
        password_min_length: values?.minLength,
        password_max_length: values?.maxLength,
        password_min_uppercase: values?.upperChar,
        password_min_lowercase: values?.lowerChar,
        password_min_special_char: values?.splChar,
        password_expiry_time: values?.expPassword,
      };
      updatePassword(orgName, payload);
    },
  });

  useEffect(() => {
    getPassword(orgName).then((resp) => {
      formikForm.setValues({
        expPassword: resp?.data?.password_expiry_time,
        minLength: resp?.data?.password_min_length,
        splChar: resp?.data?.password_min_special_char,
        upperChar: resp?.data?.password_min_uppercase,
        lowerChar: resp?.data?.password_min_lowercase,
        maxLength: resp?.data?.password_max_length,
      });
    });
  }, [orgName]);

  const handleOnChange = useCallback(
    (e) => formikForm.handleChange(e),
    [formikForm]
  );
  const handleOnKeyDown = useCallback(
    (e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault(),
    []
  );

  return (
    <Grid padding="20px">
      <Grid container alignItems="center" item xs={12}>
        <Typography
          variant="h3"
          color={theme.palette.text.primary}
          flexGrow="1"
        >
          Password Configuration
        </Typography>
      </Grid>
      <Grid container direction="row" spacing={2} paddingTop={2} xs={12}>
        <Grid item md={4} sm={4} xs={12}>
          <TextField
            fullWidth
            label={
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <span>Expire password *</span>
                <Tooltip
                  title="The number of days the password is valid before a new
                    password is required."
                  placement="top"
                  arrow
                  sx={{ marginRight: '4px' }}
                >
                  <ErrorOutlineIcon color="primary" fontSize="small" />
                </Tooltip>
              </Box>
            }
            sx={LABEL_STYLES}
            type="number"
            placeholder="Enter expire password"
            name="expPassword"
            value={formikForm?.values?.expPassword}
            onKeyDown={handleOnKeyDown}
            onChange={handleOnChange}
            aria-label="expPassword"
            onBlur={formikForm?.handleBlur}
            helperText={
              formikForm?.errors?.expPassword &&
              formikForm?.touched?.expPassword
                ? formikForm?.errors?.expPassword
                : null
            }
            error={
              Boolean(formikForm?.errors?.expPassword) &&
              formikForm?.touched?.expPassword
            }
          />
        </Grid>
        <Grid item md={4} sm={4} xs={12}>
          <TextField
            label={
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <span>Minimum length *</span>
                <Tooltip
                  title="The minimum number of characters required for the password."
                  placement="top"
                  arrow
                  sx={{ marginRight: '4px' }}
                >
                  <ErrorOutlineIcon color="primary" fontSize="small" />
                </Tooltip>
              </Box>
            }
            sx={LABEL_STYLES}
            type="number"
            placeholder="Enter minimum length"
            name="minLength"
            value={formikForm?.values?.minLength}
            onKeyDown={handleOnKeyDown}
            onChange={handleOnChange}
            aria-label="minLength"
            onBlur={formikForm?.handleBlur}
            helperText={
              formikForm?.errors?.minLength && formikForm?.touched?.minLength
                ? formikForm?.errors?.minLength
                : null
            }
            error={
              Boolean(formikForm?.errors?.minLength) &&
              formikForm?.touched?.minLength
            }
          />
        </Grid>
        <Grid item md={4} sm={4} xs={12}>
          <TextField
            label={
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <span>Special characters *</span>
                <Tooltip
                  title="The number of special characters required in the password
                    string."
                  placement="top"
                  arrow
                  sx={{ marginRight: '4px' }}
                >
                  <ErrorOutlineIcon color="primary" fontSize="small" />
                </Tooltip>
              </Box>
            }
            sx={LABEL_STYLES}
            type="number"
            placeholder="Enter special characters"
            name="splChar"
            value={formikForm?.values?.splChar}
            onKeyDown={handleOnKeyDown}
            onChange={handleOnChange}
            aria-label="splChar"
            onBlur={formikForm?.handleBlur}
            helperText={
              formikForm?.errors?.splChar && formikForm?.touched?.splChar
                ? formikForm?.errors?.splChar
                : null
            }
            error={
              Boolean(formikForm?.errors?.splChar) &&
              formikForm?.touched?.splChar
            }
          />
        </Grid>
        <Grid item md={4} sm={4} xs={12}>
          <TextField
            label={
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <span>Uppercase characters *</span>
                <Tooltip
                  title="The number of uppercase letters required in the password
                    string."
                  placement="top"
                  arrow
                  sx={{ marginRight: '4px' }}
                >
                  <ErrorOutlineIcon color="primary" fontSize="small" />
                </Tooltip>
              </Box>
            }
            sx={LABEL_STYLES}
            type="number"
            placeholder="Enter uppercase characters"
            name="upperChar"
            value={formikForm?.values?.upperChar}
            onKeyDown={handleOnKeyDown}
            onChange={handleOnChange}
            aria-label="upperChar"
            onBlur={formikForm?.handleBlur}
            helperText={
              formikForm?.errors?.upperChar && formikForm?.touched?.upperChar
                ? formikForm?.errors?.upperChar
                : null
            }
            error={
              Boolean(formikForm?.errors?.upperChar) &&
              formikForm?.touched?.upperChar
            }
          />
        </Grid>
        <Grid item md={4} sm={4} xs={12}>
          <TextField
            label={
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <span>Lowercase characters *</span>
                <Tooltip
                  title="The number of lowercase letters required in the password
                    string."
                  placement="top"
                  arrow
                  sx={{ marginRight: '4px' }}
                >
                  <ErrorOutlineIcon color="primary" fontSize="small" />
                </Tooltip>
              </Box>
            }
            sx={LABEL_STYLES}
            type="number"
            placeholder="Enter lowercase characters"
            name="lowerChar"
            value={formikForm?.values?.lowerChar}
            onKeyDown={handleOnKeyDown}
            onChange={handleOnChange}
            aria-label="lowerChar"
            onBlur={formikForm?.handleBlur}
            helperText={
              formikForm?.errors?.lowerChar && formikForm?.touched?.lowerChar
                ? formikForm?.errors?.lowerChar
                : null
            }
            error={
              Boolean(formikForm?.errors?.lowerChar) &&
              formikForm?.touched?.lowerChar
            }
          />
        </Grid>
        <Grid item md={4} sm={4} xs={12}>
          <TextField
            label={
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
              >
                <span>Maximum length *</span>
                <Tooltip
                  title="The maximum number of characters allowed in the password."
                  placement="top"
                  arrow
                  sx={{ marginRight: '4px' }}
                >
                  <ErrorOutlineIcon color="primary" fontSize="small" />
                </Tooltip>
              </Box>
            }
            sx={LABEL_STYLES}
            type="number"
            placeholder="Enter maximum length"
            name="maxLength"
            value={formikForm?.values?.maxLength}
            onKeyDown={handleOnKeyDown}
            onChange={handleOnChange}
            aria-label="maxLength"
            onBlur={formikForm?.handleBlur}
            helperText={
              formikForm?.errors?.maxLength && formikForm?.touched?.maxLength
                ? formikForm?.errors?.maxLength
                : null
            }
            error={
              Boolean(formikForm?.errors?.maxLength) &&
              formikForm?.touched?.maxLength
            }
          />
        </Grid>
      </Grid>

      <Grid container justifyContent="flex-start" mt={3}>
        <Box mr="12px">
          <Button
            variant="outlined"
            onClick={() => {
              formikForm.handleReset();
            }}
            title="Reset"
          >
            Reset
          </Button>
        </Box>
        <Box>
          <Button
            variant="contained"
            onClick={formikForm.handleSubmit}
            title="Submit"
          >
            Submit
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};

export default withTransaction('PasswordSetting', 'component')(PasswordSetting);
